import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { isAuthenticated, publishSessionProfile, refreshSessionProfile } from "./authService";
import { AUTH_TOKEN_KEY } from "./apiClient";
import { clearSessionProfile, getSessionProfile, normalizeSessionProfile } from "./sessionProfile";

const emptyProfile = () => normalizeSessionProfile(null);

export const SessionProfileContext = createContext({
  profile: emptyProfile(),
  status: "loading",
  refresh: async () => {},
});

export function SessionProfileProvider({ children }) {
  const generation = useRef(0);
  const mounted = useRef(true);
  const [session, setSession] = useState(() => ({
    profile: getSessionProfile(),
    status: isAuthenticated() ? "loading" : "unauthenticated",
  }));

  const refresh = useCallback(async () => {
    const requestGeneration = ++generation.current;
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token || !isAuthenticated()) {
      clearSessionProfile();
      setSession({ profile: emptyProfile(), status: "unauthenticated" });
      return;
    }
    setSession((current) => ({ ...current, status: "loading" }));
    try {
      const profile = await refreshSessionProfile();
      const isCurrent = mounted.current
        && requestGeneration === generation.current
        && token === localStorage.getItem(AUTH_TOKEN_KEY)
        && isAuthenticated();
      if (!isCurrent) return;
      setSession({ profile: publishSessionProfile(profile), status: "ready" });
    } catch (_) {
      const isCurrent = mounted.current
        && requestGeneration === generation.current
        && token === localStorage.getItem(AUTH_TOKEN_KEY)
        && isAuthenticated();
      if (!isCurrent) return;
      clearSessionProfile();
      setSession({ profile: emptyProfile(), status: "error" });
    }
  }, []);

  useEffect(() => {
    // React StrictMode may clean up and immediately re-run this effect in development.
    // A new effect lifetime is allowed to publish its own current refresh.
    mounted.current = true;
    refresh();
    const onSessionChange = () => {
      generation.current += 1;
      refresh();
    };
    window.addEventListener("autotuber-auth-session-changed", onSessionChange);
    return () => {
      mounted.current = false;
      generation.current += 1;
      window.removeEventListener("autotuber-auth-session-changed", onSessionChange);
    };
  }, [refresh]);

  return <SessionProfileContext.Provider value={{ ...session, refresh }}>{children}</SessionProfileContext.Provider>;
}

export const useSessionProfile = () => useContext(SessionProfileContext);
